import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserAccounts, updateUserStatus } from "../../redux/admin/userAccounts.thunk";
import { setUserCurrentPage } from "../../redux/admin/userAccounts.slice";
import toastr from "toastr";
import "toastr/build/toastr.min.css";

const UserAccounts = () => {
  const dispatch = useDispatch();
  const limit = 10;

  const {
    users = [],
    totalPages = 1,
    currentPage = 1,
    total = 0,
    loading = false,
  } = useSelector((state) => state.admin?.userAccounts || {});

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 600);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    dispatch(fetchUserAccounts({ page: currentPage, limit, search: debouncedSearch }));
  }, [dispatch, currentPage, debouncedSearch]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) dispatch(setUserCurrentPage(page));
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      await dispatch(updateUserStatus({ id, isActive: currentStatus === 0 })).unwrap();
      toastr.success("Access permissions synchronized");
    } catch (err) {
      toastr.error("Sync failed: Administrative override required");
    }
  };

  const renderPagination = () => {
    let pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + maxVisible - 1);
    
    for (let i = start; i <= end; i++) {
      pages.push(
        <button key={i} onClick={() => handlePageChange(i)}
          className={`acad-page-btn ${currentPage === i ? "active" : ""}`}>
          {i}
        </button>
      );
    }
    return pages;
  };

  return (
    <div className="acad-container">
      <div className="acad-content-wrapper">
        
        {/* APP HEADER */}
        <header className="acad-header">
          <div className="header-left">
            <h1 className="acad-title">Account Management</h1>
            <div className="acad-stats">
              <span className="stat-item"><i className="fas fa-circle text-success me-2"></i>{total} Total Identities</span>
              <span className="stat-divider"></span>
              <span className="stat-item">Academy Node: 01</span>
            </div>
          </div>
          
          <div className="header-right">
            <div className="acad-search-box">
              <i className="fas fa-search"></i>
              <input 
                type="text" 
                placeholder="Find users..." 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)} 
              />
            </div>
          </div>
        </header>

        {/* DATA CARD */}
        <div className="acad-card">
          <div className="table-responsive">
            <table className="acad-table">
              <thead>
                <tr>
                  <th className="ps-4">Identity</th>
                  <th>Designation</th>
                  <th>Organization</th>
                  <th>Privileges</th>
                  <th className="text-end pe-4">Access Control</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="5" className="text-center py-5"><div className="acad-spinner"></div></td></tr>
                ) : users.length === 0 ? (
                  <tr><td colSpan="5" className="text-center py-5 text-muted">No records matching the query.</td></tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id} className="acad-row">
                      <td className="ps-4">
                        <div className="acad-user-cell">
                          <div className={`acad-avatar ${user.is_active ? 'active' : 'inactive'}`}>
                            {user.name?.[0]}
                          </div>
                          <div className="user-info">
                            <span className="user-name">{user.name}</span>
                            <span className="user-email">{user.email}</span>
                          </div>
                        </div>
                      </td>
                      <td><span className="acad-badge-role">{user.role_name}</span></td>
                      <td><span className="acad-text-org">{user.organization_name || "Academy Core"}</span></td>
                      <td>
                        <div className="acad-tags">
                          {user.permissions?.slice(0, 2).map((p) => (
                            <span key={p} className="acad-tag">{p}</span>
                          ))}
                        </div>
                      </td>
                      <td className="text-end pe-4">
                        <div className="acad-status-pill-toggle">
                          <div className={`status-indicator ${user.is_active ? 'active' : ''}`}>
                            <span className="dot"></span>
                            <span className="label">{user.is_active ? 'Live' : 'Off'}</span>
                          </div>
                          <div className="form-check form-switch m-0 p-0">
                            <input 
                              className="form-check-input acad-switch" 
                              type="checkbox"
                              checked={user.is_active === 1}
                              onChange={() => handleToggleStatus(user.id, user.is_active)}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* TABLE FOOTER */}
          <footer className="acad-card-footer">
            <p className="acad-record-count">Records 1-{users.length} of {total}</p>
            <div className="acad-pagination">
              <button className="acad-nav-btn" disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}>
                <i className="fas fa-chevron-left"></i>
              </button>
              {renderPagination()}
              <button className="acad-nav-btn" disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)}>
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </footer>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

        .acad-container {
          background: #fcfcfd;
          min-height: 100vh;
          font-family: 'Inter', sans-serif;
          color: #101828;
          padding: 2rem;
        }

        .acad-content-wrapper {
          max-width: 1200px;
          margin: 0 auto;
        }

        /* HEADER */
        .acad-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          margin-bottom: 24px;
        }

        .acad-title {
          font-size: 1.5rem;
          font-weight: 700;
          letter-spacing: -0.02em;
          margin-bottom: 8px;
        }

        .acad-stats {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 0.8rem;
          color: #667085;
          font-weight: 500;
        }

        .stat-divider { width: 1px; height: 12px; background: #eaecf0; }

        .acad-search-box {
          background: #fff;
          border: 1px solid #d0d5dd;
          border-radius: 8px;
          padding: 8px 14px;
          display: flex;
          align-items: center;
          width: 300px;
          box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
        }

        .acad-search-box i { color: #667085; font-size: 0.9rem; }
        .acad-search-box input {
          border: none;
          outline: none;
          margin-left: 10px;
          font-size: 0.85rem;
          width: 100%;
        }

        /* TABLE CARD */
        .acad-card {
          background: #fff;
          border: 1px solid #eaecf0;
          border-radius: 12px;
          box-shadow: 0 4px 6px -2px rgba(16, 24, 40, 0.03), 0 12px 16px -4px rgba(16, 24, 40, 0.08);
          overflow: hidden;
        }

        .acad-table { width: 100%; border-collapse: collapse; }
        .acad-table thead th {
          background: #f9fafb;
          padding: 12px 16px;
          font-size: 0.75rem;
          font-weight: 600;
          color: #475467;
          border-bottom: 1px solid #eaecf0;
          text-align: left;
        }

        .acad-row { border-bottom: 1px solid #eaecf0; transition: background 0.2s; }
        .acad-row:hover { background: #f9fafb; }

        /* USER CELL */
        .acad-user-cell { display: flex; align-items: center; gap: 12px; padding: 12px 0; }
        .acad-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          color: #fff;
          background: #98a2b3;
        }
        .acad-avatar.active { background: #4f46e5; }

        .user-name { display: block; font-size: 0.875rem; font-weight: 600; color: #101828; }
        .user-email { display: block; font-size: 0.8rem; color: #667085; }

        .acad-badge-role {
          background: #f2f4f7;
          color: #344054;
          padding: 2px 8px;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 500;
          border: 1px solid #d0d5dd;
        }

        .acad-text-org { font-size: 0.85rem; color: #475467; font-weight: 500; }

        .acad-tag {
          background: #eff8ff;
          color: #175cd3;
          font-size: 0.7rem;
          padding: 2px 8px;
          border-radius: 4px;
          margin-right: 4px;
        }

        /* REFINED TOGGLE */
        .acad-status-pill-toggle {
          display: inline-flex;
          align-items: center;
          background: #f9fafb;
          border: 1px solid #eaecf0;
          padding: 4px 8px 4px 12px;
          border-radius: 100px;
          gap: 12px;
        }

        .status-indicator { display: flex; align-items: center; gap: 6px; }
        .status-indicator .dot { width: 6px; height: 6px; border-radius: 50%; background: #d0d5dd; }
        .status-indicator.active .dot { background: #12b76a; box-shadow: 0 0 8px rgba(18, 183, 106, 0.4); }
        .status-indicator .label { font-size: 0.7rem; font-weight: 700; color: #344054; }

        .acad-switch { width: 30px !important; height: 16px !important; cursor: pointer; }
        .acad-switch:checked { background-color: #101828 !important; border-color: #101828 !important; }

        /* FOOTER */
        .acad-card-footer {
          padding: 12px 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: #fff;
        }

        .acad-record-count { font-size: 0.8rem; color: #475467; font-weight: 500; margin: 0; }
        .acad-pagination { display: flex; gap: 4px; }
        
        .acad-page-btn {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          border: 1px solid #d0d5dd;
          background: #fff;
          font-size: 0.8rem;
          font-weight: 600;
          color: #1d2939;
        }

        .acad-page-btn.active { background: #f9fafb; border-color: #d0d5dd; color: #4f46e5; }
        .acad-nav-btn { width: 32px; height: 32px; border: 1px solid #d0d5dd; border-radius: 8px; background: #fff; color: #667085; }
        .acad-nav-btn:disabled { opacity: 0.5; cursor: not-allowed; }

        .acad-spinner { 
          width: 24px; height: 24px; border: 2px solid #f3f3f3; border-top: 2px solid #4f46e5; 
          border-radius: 50%; animation: spin 0.8s linear infinite; display: inline-block; 
        }
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default UserAccounts;